/**
 * @fileoverview Syndicator Showdown — Stable Share Service
 * @module services/stableShareService
 *
 * Creates and reads `stable_shares/{shareId}` — a privacy-safe SNAPSHOT of a
 * player's stable, built once client-side from data the owner already has
 * read access to (their own player_entries + the public game_slates/races).
 *
 * The public page (ShareStablePage) never reads player_entries directly —
 * only this snapshot, which intentionally excludes email, uid-embedding
 * entryId, wallet internals (remainingBankroll/totalBankroll), and the raw
 * player_entries document shape. See firestore.rules for the read/write
 * rules this depends on.
 */

import {
  doc,
  collection,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from './firebase';
import { getPublicName, getProfileImageUrl } from '../utils/profileImage';
import { getOrCreateReferralCode } from './referralService';

function toMsLocal(val) {
  if (!val) return null;
  if (typeof val === 'number') return val;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (val._seconds !== undefined) return val._seconds * 1000;
  const ms = new Date(val).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Firestore auto-IDs are short, URL-safe and effectively collision-proof. */
function makeShareId() {
  return doc(collection(db, 'stable_shares')).id;
}

/**
 * Builds the public snapshot from already-loaded entry/allocation/race data.
 * Never re-reads Firestore itself — the caller supplies everything, since it
 * already has read access to its own player_entries + race_allocations.
 *
 * @param {Object} params
 * @param {Object} params.slate       - game_slates/{slateId} data (public)
 * @param {Object} params.entry       - player_entries/{entryId} data (owner-only)
 * @param {Object} params.allocations - { [raceId]: race_allocations doc data }
 * @param {Array}  params.races       - game_slates/{slateId}/races docs (public)
 * @returns {Object} snapshot ready to embed in stable_shares/{shareId}.snapshot
 */
export function buildStableSnapshot({ slate, entry, allocations, races }) {
  const raceLookup = new Map((races || []).map((r) => [r.id, r]));

  const raceSnapshots = Object.entries(allocations || {})
    .map(([raceId, alloc]) => {
      const raceMeta = raceLookup.get(raceId) || {};
      const runnerLookup = new Map(
        (Array.isArray(raceMeta.runners) ? raceMeta.runners : []).map((r) => [r.id, r])
      );

      const selectedRunners = Object.entries(alloc.runners || {})
        .filter(([, r]) => (r.equityPercent || 0) > 0)
        .map(([runnerId, r]) => {
          const runnerMeta = runnerLookup.get(runnerId) || {};
          const equityPercent = r.equityPercent || 0;
          const purchaseCost  = r.purchaseCost || 0;
          return {
            runnerNumber:  runnerMeta.runnerNumber ?? null,
            runnerName:    runnerMeta.name || runnerId,
            equityPercent,
            // Derived (not separately stored per-purchase) — matches the
            // "price per 1% equity" figure shown throughout the app.
            sharePrice:    equityPercent > 0 ? Math.round(purchaseCost / equityPercent) : null,
            purchaseCost,
            displayRating: runnerMeta.displayRating ?? null,
            valueBadge:    runnerMeta.valueBadge && runnerMeta.valueBadge !== 'UNKNOWN'
              ? runnerMeta.valueBadge
              : null,
          };
        })
        .sort((a, b) => (b.equityPercent || 0) - (a.equityPercent || 0));

      return {
        raceId,
        raceNumber:       raceMeta.raceNumber ?? null,
        raceName:         raceMeta.raceName || null,
        meetingName:      raceMeta.meetingName || raceMeta.meetingState || null,
        totalSpentOnRace: alloc.totalSpentOnRace || 0,
        selectedRunners,
      };
    })
    // Only keep races with at least one active selection — an empty race
    // adds nothing to a visitor's view of the stable.
    .filter((r) => r.selectedRunners.length > 0)
    .sort((a, b) => (a.raceNumber ?? 0) - (b.raceNumber ?? 0));

  const totalRunners = raceSnapshots.reduce((sum, r) => sum + r.selectedRunners.length, 0);

  return {
    gameName:    slate?.gameName || slate?.id || 'Showdown',
    meetingDate: slate?.meetingDate || null,
    totalSpend:  entry?.totalSpent || 0,
    totalRunners,
    races:       raceSnapshots,
  };
}

/**
 * Creates a new public share document for a stable. Returns the new shareId.
 * Phase 1 keeps this simple — every call creates a fresh document rather than
 * trying to dedupe/reuse a previous share for the same entry.
 *
 * @param {Object} params
 * @param {import('firebase/auth').User} params.user
 * @param {Object|null} params.userDoc - users/{uid} data, for public identity/avatar fallback
 * @param {string} params.slateId
 * @param {'immediate'|'after_entries_close'} params.revealMode
 * @param {Object} params.snapshot - output of buildStableSnapshot()
 * @returns {Promise<string>} shareId
 */
export async function createStableShare({ user, userDoc, slateId, revealMode, snapshot }) {
  const shareId = makeShareId();

  // Phase 6 — best-effort so the public page's "Join the Showdown"/
  // "Challenge This Stable" CTAs can preserve attribution back to the
  // owner using the SAME referral pipeline already trusted at sign-up
  // (AuthPage.ensureUserDocument -> resolveStoredReferral). Never blocks
  // share creation if it fails.
  let ownerReferralCode = userDoc?.referralCode || null;
  if (!ownerReferralCode) {
    try {
      ownerReferralCode = await getOrCreateReferralCode(user, userDoc);
    } catch (err) {
      console.warn('[stableShareService] getOrCreateReferralCode failed (non-blocking):', err?.message);
    }
  }

  await setDoc(doc(db, 'stable_shares', shareId), {
    shareId,
    ownerUid:         user.uid,
    // Public page — Stable Name (username) is the public identity, never
    // the owner's real displayName (see getPublicName priority order).
    ownerDisplayName: getPublicName(userDoc, user),
    ownerPhotoURL:    getProfileImageUrl(userDoc, user) || null,
    ownerReferralCode,
    slateId,
    visibility:       'public',
    revealMode,
    createdAt:        serverTimestamp(),
    updatedAt:        serverTimestamp(),
    snapshot,
    // Phase 6 share tracking — safe aggregate counters only (see
    // functions/src/share/trackShareEvent.js, the only writer of these
    // fields post-creation).
    views:              0,
    uniqueViewEstimate: 0,
    copyClicks:         0,
    nativeShareClicks:  0,
    qrOpens:            0,
    challengeClicks:    0,
    signupsAttributed:  0,
    lastViewedAt:       null,
    lastSharedAt:       null,
  });

  return shareId;
}

/**
 * Escalates an existing share to reveal full selections immediately —
 * used when a player explicitly confirms "reveal my selections now" before
 * entries have closed.
 *
 * @param {string} shareId
 */
export async function revealStableShareNow(shareId) {
  await updateDoc(doc(db, 'stable_shares', shareId), {
    revealMode: 'immediate',
    updatedAt:  serverTimestamp(),
  });
}

/**
 * Public — fetches a stable share document for the public share page.
 *
 * @param {string} shareId
 * @returns {Promise<Object|null>}
 */
export async function fetchStableShare(shareId) {
  const snap = await getDoc(doc(db, 'stable_shares', shareId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Reuses this player's most recent share for a given slate, or creates a
 * fresh one if none exists yet — used by result-sharing surfaces (Share
 * Result on a race/leaderboard row) that need a public link but shouldn't
 * spawn a brand new stable_shares document every single click the way the
 * dedicated "Share My Stable" button intentionally does.
 *
 * @param {Object} params
 * @param {import('firebase/auth').User} params.user
 * @param {Object|null} params.userDoc
 * @param {Object} params.entry - player_entries doc (needs id, slateId, totalSpent)
 * @returns {Promise<string>} shareId
 */
export async function getOrCreateStableShareForEntry({ user, userDoc, entry }) {
  const existing = await getDocs(
    query(
      collection(db, 'stable_shares'),
      where('ownerUid', '==', user.uid),
      where('slateId', '==', entry.slateId)
    )
  );

  if (!existing.empty) {
    const newest = existing.docs.reduce((best, d) => {
      const ms = toMsLocal(d.data()?.createdAt);
      return !best || ms > best.ms ? { id: d.id, ms: ms || 0 } : best;
    }, null);
    if (newest) return newest.id;
  }

  const [slateSnap, racesSnap, allocSnap] = await Promise.all([
    getDoc(doc(db, 'game_slates', entry.slateId)),
    getDocs(query(collection(db, 'game_slates', entry.slateId, 'races'), orderBy('raceNumber', 'asc'))),
    getDocs(collection(db, 'player_entries', entry.id, 'race_allocations')),
  ]);

  const slate = slateSnap.exists() ? { id: slateSnap.id, ...slateSnap.data() } : null;
  const races = racesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const allocations = {};
  allocSnap.docs.forEach((d) => { allocations[d.id] = d.data(); });

  const closeMs = toMsLocal(slate?.gameCloseTime);
  const entriesClosed = !!slate?.finalised || (!!closeMs && Date.now() >= closeMs);
  const revealMode = entriesClosed ? 'immediate' : 'after_entries_close';
  const snapshot = buildStableSnapshot({ slate, entry, allocations, races });

  return createStableShare({ user, userDoc, slateId: entry.slateId, revealMode, snapshot });
}

/**
 * Owner-only — aggregates this player's own share performance for the
 * lightweight "Shared 8 times · 24 views" stat shown on My Stable / inside
 * the share modal. Firestore rules only allow this query when it's scoped
 * to the caller's own ownerUid (see firestore.rules stable_shares#list).
 *
 * Never throws — returns a zeroed stat object on any failure so the UI can
 * fail quiet rather than show an error for a nice-to-have metric.
 *
 * @param {string} uid
 * @returns {Promise<{ shareCount: number, totalViews: number }>}
 */
export async function getMyShareStats(uid) {
  if (!uid) return { shareCount: 0, totalViews: 0 };
  try {
    const snap = await getDocs(query(collection(db, 'stable_shares'), where('ownerUid', '==', uid)));
    let totalViews = 0;
    snap.docs.forEach((d) => { totalViews += d.data()?.views || 0; });
    return { shareCount: snap.size, totalViews };
  } catch (err) {
    console.warn('[stableShareService] getMyShareStats failed (non-blocking):', err?.message);
    return { shareCount: 0, totalViews: 0 };
  }
}
